import { useMemo } from 'react';
import { CheckCircle, FileText, Calendar, TrendingUp } from 'lucide-react';
import {
  useWellnessCompletionRate,
  useWellnessMetrics as useWellnessMetricsData,
} from './useWellnessAnalytics';
import { chartUtils } from '../../components/analytics/utils/chartUtils';
import { METRICS_CONFIG } from '../../components/analytics/config';
import { CurrentMonthLabel } from '../../components/analytics/shared/CurrentMonthLabel';
import { findBestMetricImprovement } from '../../components/analytics/wellness/wellnessUtils';
import { WellnessFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';

const ICON_SIZE = 'w-4 h-4';

export function useWellnessKPIData(filters: WellnessFilter) {
  const { data: completionData, isLoading: completionLoading } = useWellnessCompletionRate(filters);
  const { data: metricsData, isLoading: metricsLoading } = useWellnessMetricsData(filters);

  const isLoading = completionLoading;
  const hasEndDateFilter = Boolean(filters?.endDate);

  const bestMetric = useMemo(() => {
    if (!metricsData) return { name: 'No Data', actual: 0, questionkey: null };
    return findBestMetricImprovement(metricsData);
  }, [metricsData]);

  const kpiItems = useMemo(() => {
    if (!completionData) return [];

    const data = completionData;
    const createKPI = (
      id: string,
      label: string,
      value: string,
      monthValue: string,
      icon: JSX.Element,
      accent: string,
      options?: {
        hideSecondaryOnFilter?: boolean;
        secondaryLabel?: JSX.Element | string | null;
        isFormScoped?: boolean;
      },
    ) => {
      const hideSecondaryOnFilter = options?.hideSecondaryOnFilter ?? true;
      const secondaryLabel = options?.secondaryLabel ?? <CurrentMonthLabel />;
      const shouldHideSecondary = hideSecondaryOnFilter && hasEndDateFilter;

      return {
        id,
        label,
        value,
        secondaryValue: shouldHideSecondary
          ? undefined
          : {
              value: monthValue,
              label: secondaryLabel ?? undefined,
              color: accent,
            },
        icon,
        color: accent,
        isFormScoped: options?.isFormScoped ?? false,
      };
    };

    return [
      createKPI(
        'total-sessions',
        'Total Sessions',
        chartUtils.formatNumber(data.total_sessions),
        chartUtils.formatNumber(data.this_month_sessions),
        <Calendar className={ICON_SIZE} />,
        '#3c57a8',
      ),
      createKPI(
        'completed-forms',
        'Completed Forms',
        chartUtils.formatNumber(data.completed_sessions),
        chartUtils.formatNumber(data.this_month_completed),
        <FileText className={ICON_SIZE} />,
        '#00a8f0',
      ),
      createKPI(
        'completion-rate',
        'Completion Rate',
        `${data.completion_rate.toFixed(1)}%`,
        `${data.this_month_completion_rate.toFixed(1)}%`,
        <CheckCircle className={ICON_SIZE} />,
        '#e23846',
      ),
      createKPI(
        'best-improvement',
        'Most Improved',
        metricsLoading
          ? METRICS_CONFIG.DEFAULT_STRING
          : `${bestMetric.actual > 0 ? '+' : ''}${bestMetric.actual.toFixed(1)}%`,
        metricsLoading ? METRICS_CONFIG.DEFAULT_STRING : bestMetric.name,
        <TrendingUp className={ICON_SIZE} />,
        '#e69b0d',
        { hideSecondaryOnFilter: false, secondaryLabel: null, isFormScoped: true },
      ),
    ];
  }, [completionData, bestMetric, metricsLoading, hasEndDateFilter]);

  return { kpiItems, isLoading };
}
